import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';

import getFirstSubdomain from '../shared-functions/DomiansplitFunction';
import brandingService, { BrandingData } from '../utils/api/branding';
import { generateFullTheme } from '../utils/brandingUtils';

interface BrandingContextType {
    brandData: BrandingData | null;
    isLoading: boolean;
    error: string | null;
    refreshBranding: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

/**
 * Injects theme CSS variables into the document root and activates data-brand tokens.
 * @param {any} themeConfig - The theme configuration object containing color definitions.
 * @param {string} [brandName] - Optional brand preset identifier ('voltie' | 'energic').
 * @returns {void}
 */
export const applyThemeToDocument = (themeConfig: any, brandName?: string): void => {
    const domain = getFirstSubdomain() || '';
    const resolvedBrand = brandName || (domain === 'voltie' ? 'voltie' : 'energic');

    if (document.documentElement.getAttribute('data-brand') !== resolvedBrand) {
        document.documentElement.setAttribute('data-brand', resolvedBrand);
    }

    if (resolvedBrand === 'voltie' && (!themeConfig || Object.keys(themeConfig).length === 0)) {
        document.documentElement.style.setProperty('--primary', '#39b6ff');
        document.documentElement.style.setProperty('--primary-color', '#39b6ff');
        document.documentElement.style.setProperty('--gradient-primary', '#0d88d0');
        document.documentElement.style.setProperty('--gradient-green', '#0d88d0');
        document.documentElement.style.setProperty('--battery-loader-2', '#0d88d0');
        return;
    }

    if (!themeConfig) return;

    // Generate full theme variables (light and dark)
    const fullTheme = generateFullTheme(themeConfig);

    // Determine current mode (default to light or get from localStorage)
    const currentMode = localStorage.getItem('globalTheme') || 'light';
    const themeVariables = fullTheme[currentMode] || fullTheme.light || fullTheme;

    if (themeVariables) {
        Object.entries(themeVariables).forEach(([key, value]) => {
            document.documentElement.style.setProperty(`--${key}`, value as string);
        });

        const primary = themeVariables['primary-color'] || themeVariables.primary;
        if (primary) {
            document.documentElement.style.setProperty('--primary', primary as string);
            document.documentElement.style.setProperty('--primary-color', primary as string);
        }
        const gradient =
            themeVariables['gradient-primary'] ||
            themeVariables['gradient-green'] ||
            primary;
        if (gradient) {
            document.documentElement.style.setProperty('--gradient-primary', gradient as string);
            document.documentElement.style.setProperty('--gradient-green', gradient as string);
            document.documentElement.style.setProperty('--battery-loader-2', gradient as string);
        }
    }
};

/**
 * Updates document metadata like title and favicon based on branding data.
 * @param {BrandingData} data - The branding data.
 * @returns {void}
 */
const updateDocumentMetadata = (data: BrandingData): void => {
    if (data.clientName) {
        document.title = data.clientName;
    }

    if (data.favicon) {
        const link = document.querySelector("link[rel*='icon']");
        if (link instanceof HTMLLinkElement) {
            link.href = data.favicon;
        } else {
            const newLink = document.createElement('link');
            newLink.rel = 'icon';
            newLink.href = data.favicon;
            document.head.appendChild(newLink);
        }
    }

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && data.clientName) {
        metaDescription.setAttribute('content', data.clientName);
    }
};

/**
 * BrandingProvider component that fetches and manages runtime branding data.
 * @param props - Component properties.
 * @param props.children - Child elements.
 * @returns The provider component.
 */
export function BrandingProvider({ children }: Readonly<{ children: ReactNode }>): JSX.Element {
    // Synchronous initial cache hydration on frame 0 to eliminate initial theme flash
    const initialBrandData = useMemo(() => {
        const cached = brandingService.getCachedBranding();
        if (cached) {
            applyThemeToDocument(cached.theme, cached.branding);
            updateDocumentMetadata(cached);
            return cached;
        }
        const domain = getFirstSubdomain() || '';
        const baseBrand = domain === 'voltie' ? 'voltie' : 'energic';
        applyThemeToDocument(null, baseBrand);
        return null;
    }, []);

    const [brandData, setBrandData] = useState<BrandingData | null>(initialBrandData);
    const [isLoading, setIsLoading] = useState<boolean>(!initialBrandData);
    const [error, setError] = useState<string | null>(null);

    /**
     * Fetches branding data from the API and updates state.
     * @returns {Promise<void>}
     */
    const fetchBranding = useCallback(async (): Promise<void> => {
        try {
            const data = await brandingService.fetchBranding();
            if (data) {
                setBrandData((previousBrandData) => {
                    if (
                        previousBrandData &&
                        JSON.stringify(previousBrandData) === JSON.stringify(data)
                    ) {
                        return previousBrandData;
                    }
                    applyThemeToDocument(data.theme, data.branding);
                    updateDocumentMetadata(data);
                    return data;
                });
            } else {
                brandingService.resetFavicon();
            }
            setError(null);
        } catch (error_) {
            brandingService.resetFavicon();
            setError('Failed to load branding');
            console.error(error_);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBranding();
    }, [fetchBranding]);

    useEffect(() => {
        /**
         * Handles storage changes to re-apply themes if the global mode changes.
         * @param {StorageEvent} event - The storage event.
         * @returns {void}
         */
        const handleStorageChange = (event: StorageEvent): void => {
            if (event.key === 'globalTheme' && brandData?.theme) {
                applyThemeToDocument(brandData.theme, brandData.branding);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [brandData]);

    // Re-apply theme if brandData changes
    useEffect(() => {
        if (brandData?.theme) {
            applyThemeToDocument(brandData.theme, brandData.branding);
        }
    }, [brandData]);

    const contextValue = useMemo(
        () => ({
            brandData,
            isLoading,
            error,
            refreshBranding: fetchBranding,
        }),
        [brandData, isLoading, error, fetchBranding],
    );

    // If still loading and we don't have cached data yet, show a simple loader to avoid default branding flash
    if (isLoading && !brandData) {
        return (
            <div className="branding-initial-loader">
                <div className="loader-inner">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden"/>
                    </div>
                </div>
            </div>
        );
    }

    return <BrandingContext.Provider value={contextValue}>{children}</BrandingContext.Provider>;
}

/**
 * Custom hook to consume branding data from the BrandingContext.
 * @returns The branding context data.
 */
export const useBranding = () => {
    const context = useContext(BrandingContext);
    if (context === undefined) {
        throw new Error('useBranding must be used within a BrandingProvider');
    }
    return context;
};
